import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

/**
 * SEO — Reusable component for per-page SEO meta tags with i18n + hreflang.
 * Usage: <SEO title="..." description="..." path="/contact" />
 */
const SEO = ({
  title,
  description,
  path = '/',
  type = 'website',
  noindex = false,
  jsonLd = null,
}) => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const siteUrl = 'https://reviewsight.io';
  const siteName = 'ReviewSight';

  const defaultSubtitle = lang === 'en'
    ? 'Collaborative Visual QA Platform'
    : 'Plateforme de QA visuelle collaborative';
  const fullTitle = title ? `${title} | ${siteName}` : `${siteName} — ${defaultSubtitle}`;

  // Canonical: French = bare path, English = /en/path
  const frPath = path.replace(/^\/en(\/|$)/, '/');
  const enPath = frPath === '/' ? '/en' : `/en${frPath}`;
  const canonicalUrl = `${siteUrl}${lang === 'en' ? enPath : frPath}`;

  const ogLocale = lang === 'en' ? 'en_GB' : 'fr_FR';
  const altLocale = lang === 'en' ? 'fr_FR' : 'en_GB';

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang for SEO */}
      <link rel="alternate" hrefLang="fr" href={`${siteUrl}${frPath}`} />
      <link rel="alternate" hrefLang="en" href={`${siteUrl}${enPath}`} />
      <link rel="alternate" hrefLang="x-default" href={`${siteUrl}${frPath}`} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={altLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
